import { useState } from 'react'
import StackedLayout from './ui/StackedLayout'
import Navbar from './ui/Navbar'
import Sidebar from './ui/Sidebar'
import SidebarHeader from './ui/SidebarHeader'
import SidebarBody from './ui/SidebarBody'
import SidebarNavigation from './ui/SidebarNavigation'
import Dropdown from './ui/Dropdown'
import DropdownButton from './ui/DropdownButton'
import DropdownMenu from './ui/DropdownMenu'
import DropdownItem from './ui/DropdownItem'
import DropdownLabel from './ui/DropdownLabel'
import Avatar from './ui/Avatar'
import { Logo } from './ui/Logo'
import { Container } from './ui'

interface AuthenticatedStackedLayoutProps {
  children: React.ReactNode
  currentPath: string
  user: { email: string }
  onLogout: () => void
}

export default function AuthenticatedStackedLayout({
 children, currentPath, user, onLogout 
}: AuthenticatedStackedLayoutProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleLogout = () => {
    onLogout()
    setIsDropdownOpen(false)
  }

  return (
    <>
      {/* Mobile sidebar dialog */}
      <div className={`relative z-50 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-in-out ${
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        
        {/* Mobile sidebar */}
        <div className="fixed inset-0 flex">
          <div className={`relative mr-16 flex w-full max-w-xs flex-1 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
            {/* Sidebar content */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center justify-between">
                <div className="flex items-center">
                  <Logo variant="image" showText={true} size="md" />
                </div>
                {/* Close button - inside the sidebar */}
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden"
                >
                  <span className="sr-only">Close sidebar</span>
                  <svg className="size-6 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="flex flex-1 flex-col">
                <SidebarNavigation currentPath={currentPath} />
              </nav>
            </div>
          </div>
        </div>
      </div>

      <StackedLayout
        sidebar={
          <Sidebar>
            <SidebarHeader>
              <div className="flex items-center px-3">
                <Logo variant="image" showText={true} size="md" />
              </div>
            </SidebarHeader>
            <SidebarBody>
              <SidebarNavigation currentPath={currentPath} />
            </SidebarBody>
          </Sidebar>
        }
        navbar={
          <Navbar>
            <div className="grid grid-cols-1 w-full">
              <div className="flex items-center justify-between gap-x-4 lg:gap-x-6">
                {/* Mobile menu button */}
                <div className="flex items-center gap-x-3">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                  >
                    <span className="sr-only">Open sidebar</span>
                    <svg className="size-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                  </button>

                  {/* App name */}
                  <span className="text-lg font-semibold text-gray-900 lg:hidden">
                    {import.meta.env.VITE_APPLICATION_NAME || 'Simple CPRs'}
                  </span>
                </div>

                {/* Separator */}
                <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden" />

                <div className="flex items-center gap-x-4 lg:gap-x-6">
                  {/* Separator */}
                  <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" />

                  {/* Profile dropdown */}
                  <div className="relative">
                    <Dropdown>
                      <DropdownButton 
                        onClick={handleDropdownToggle}
                        className="relative flex items-center"
                        aria-expanded={isDropdownOpen}
                      >
                        <span className="absolute -inset-1.5" />
                        <span className="sr-only">Open user menu</span>
                        <Avatar initials={user.email[0].toUpperCase()} className="size-8" />
                        <span className="hidden lg:flex lg:items-center">
                          <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                            {user.email}
                          </span>
                          <svg aria-hidden="true" className="ml-2 size-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </DropdownButton>
                      {isDropdownOpen && (
                        <DropdownMenu className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5">
                          <DropdownItem onClick={handleLogout}>
                            <DropdownLabel>Sign out</DropdownLabel>
                          </DropdownItem>
                        </DropdownMenu>
                      )}
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </Navbar>
        }
      >
        <Container className="py-16">
          {children}
        </Container>
      </StackedLayout>
    </>
  )
} 